import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { signUp } from "@/services/auth";
import { openReferralLink } from "@/services/affiliate";

const fieldCls =
  "w-full pl-10 pr-4 py-3 rounded-xl bg-[#0d1a3a] border border-[#1e3260] text-white placeholder:text-foreground-400 text-sm outline-none focus:border-primary-500 transition-colors";

export default function Registro() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState<string | null | undefined>(undefined); // undefined = cargando
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    openReferralLink(code)
      .then((name) => {
        if (active) setSponsor(name);
      })
      .catch(() => {
        if (active) setSponsor(null);
      });
    return () => {
      active = false;
    };
  }, [code]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    if ((data.get("website_alt") as string | null)?.trim()) return;
    const fullName = ((data.get("name") as string) || "").trim();
    const email = ((data.get("email") as string) || "").trim();
    const password = ((data.get("password") as string) || "").trim();
    if (!fullName || !email || password.length < 6) {
      setError("Completa tu nombre, correo y una contraseña de al menos 6 caracteres.");
      return;
    }
    setLoading(true);
    try {
      const result = await signUp(email, password, { referralCode: code, fullName });
      if (result.session) navigate("/app/dashboard");
      else setInfo("¡Cuenta creada! Revisa tu correo y confirma tu cuenta para entrar.");
    } catch (err) {
      setError(
        err instanceof Error && /already registered|user already exists/i.test(err.message)
          ? "Ese correo ya tiene una cuenta. Inicia sesión desde la pantalla principal."
          : "No se pudo crear la cuenta. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B26] relative overflow-hidden flex flex-col">
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 opacity-30">
        <div className="w-full h-full rounded-full bg-gradient-to-bl from-accent-500 via-secondary-500 to-transparent blur-3xl"></div>
      </div>
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-6 max-w-md mx-auto w-full">
        <BrandLogo className="mb-6" />

        <div className="w-full rounded-2xl bg-[#0B1330]/90 border border-[#1a2a5e] p-5 md:p-7">
          {sponsor === undefined ? (
            <div className="text-center text-foreground-300 py-8">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
            </div>
          ) : sponsor === null ? (
            <div className="text-center py-4">
              <i className="ri-link-unlink text-3xl text-accent-400"></i>
              <h1 className="font-heading text-xl font-bold text-white mt-2">Este link no es válido</h1>
              <p className="text-foreground-300 text-sm mt-1">Pídele a quien te invitó que te comparta su link de nuevo.</p>
              <Link to="/" className="inline-block mt-4 text-primary-400 font-semibold text-sm">
                Ir al inicio
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-300 bg-primary-900/30 border border-primary-700/40 rounded-full px-3 py-1">
                  <i className="ri-gift-line"></i> {sponsor} te invitó a Vívelo AI
                </span>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-white mt-3">Crea tu cuenta</h1>
                <p className="text-foreground-300 text-sm mt-1">Descubre tu perfil emprendedor y empieza tu ruta.</p>
              </div>

              {info ? (
                <div className="text-sm text-primary-300 bg-primary-900/30 border border-primary-700/40 rounded-lg px-3 py-2">{info}</div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3.5">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="vv-safe-field" />
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                    <input name="name" type="text" placeholder="Tu nombre" autoComplete="name" required className={fieldCls} />
                  </div>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                    <input name="email" type="email" placeholder="Correo electrónico" autoComplete="email" required className={fieldCls} />
                  </div>
                  <div className="relative">
                    <i className="ri-lock-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                    <input name="password" type="password" placeholder="Contraseña (mínimo 6 caracteres)" autoComplete="new-password" required className={fieldCls} />
                  </div>
                  {error ? (
                    <div className="text-sm text-accent-300 bg-accent-900/40 border border-accent-700/40 rounded-lg px-3 py-2">{error}</div>
                  ) : null}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-background-950 font-heading font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-70 transition-colors"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> Creando cuenta...
                      </>
                    ) : (
                      <>
                        Crear cuenta <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-foreground-400">
                    Al registrarte, {sponsor} podrá ver tu nombre, correo y tu avance en Vívelo AI para acompañarte.
                  </p>
                  <p className="text-center text-xs text-foreground-400">
                    ¿Ya tienes cuenta?{" "}
                    <Link to="/" className="text-primary-400 font-semibold">
                      Iniciar sesión
                    </Link>
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
